import React, { useState } from 'react';
import { User, Building, Search, RefreshCw, X } from 'lucide-react';

// Création inline d'un client depuis la demande de devis courtier.
// POST /api/partner/clients puis sélection automatique du client créé.
const QuoteClientCreate = ({ backendUrl, authToken, onSelected, onCancel }) => {
  const [form, setForm] = useState({
    type: 'particulier',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    siret: '',
    company_in_creation: false,
    company_address: '',
    company_city: '',
    company_postal_code: '',
  });
  const [siretLoading, setSiretLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const isPro = form.type === 'professionnel';
  const siretClean = (form.siret || '').replace(/\s/g, '');

  const lookupSiret = async () => {
    setError('');
    if (siretClean.length !== 14 || !/^\d+$/.test(siretClean)) {
      setError('Le SIRET doit comporter 14 chiffres');
      return;
    }
    try {
      setSiretLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/companies/lookup-siret/${siretClean}`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        const d = await response.json();
        setForm((prev) => ({
          ...prev,
          siret: d.siret || siretClean,
          company: d.name || prev.company,
          company_address: d.address || prev.company_address,
          company_postal_code: d.postal_code || prev.company_postal_code,
          company_city: d.city || prev.company_city,
        }));
      } else {
        const e = await response.json().catch(() => null);
        setError(e?.detail || 'Recherche INSEE impossible');
      }
    } catch (e) {
      setError('Recherche INSEE impossible');
    } finally {
      setSiretLoading(false);
    }
  };

  const submit = async () => {
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Prénom, nom, email et téléphone sont obligatoires');
      return;
    }
    if (isPro && !form.company.trim()) {
      setError("Le nom de l'entreprise est obligatoire");
      return;
    }
    try {
      setSubmitting(true);
      const response = await fetch(`${backendUrl}/api/partner/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        const created = await response.json();
        onSelected({
          id: created.id || created.client_id,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
        });
      } else {
        const e = await response.json().catch(() => null);
        setError(e?.detail || 'Erreur lors de la création du client');
      }
    } catch (e) {
      setError('Erreur lors de la création du client');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 text-sm disabled:bg-slate-50';

  return (
    <div className="mt-3 p-4 border border-cyan-200 bg-cyan-50/40 rounded-xl space-y-3" data-testid="quote-client-create">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-800 text-sm">Nouveau client</h4>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600" data-testid="quote-client-create-cancel">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[['particulier', 'Particulier', User], ['professionnel', 'Professionnel', Building]].map(([val, label, Icon]) => (
          <button
            key={val}
            type="button"
            onClick={() => set('type', val)}
            className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-colors ${form.type === val ? 'border-cyan-500 bg-cyan-100 text-cyan-700' : 'border-slate-300 bg-white text-slate-500'}`}
            data-testid={`quote-client-type-${val}`}
          >
            <Icon size={18} />
            <span className="text-xs font-medium">{label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className={inputCls} placeholder="Prénom *" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="quote-client-first-name" />
        <input className={inputCls} placeholder="Nom *" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="quote-client-last-name" />
      </div>
      <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="quote-client-email" />
      <input className={inputCls} placeholder="Téléphone *" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="quote-client-phone" />

      {isPro && (
        <div className="space-y-2 pt-1 border-t border-cyan-200">
          <div className="flex gap-2">
            <input
              className={`flex-1 min-w-0 ${inputCls}`}
              inputMode="numeric"
              placeholder="SIRET (14 chiffres)"
              value={form.siret}
              disabled={form.company_in_creation}
              onChange={(e) => set('siret', e.target.value)}
              data-testid="quote-client-siret"
            />
            <button
              type="button"
              onClick={lookupSiret}
              disabled={form.company_in_creation || siretLoading || siretClean.length !== 14}
              className="px-4 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="quote-client-siret-lookup"
            >
              {siretLoading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
              <span className="hidden sm:inline text-sm">Rechercher</span>
            </button>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.company_in_creation}
              onChange={(e) => setForm((prev) => ({ ...prev, company_in_creation: e.target.checked, siret: e.target.checked ? '' : prev.siret }))}
              className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              data-testid="quote-client-in-creation"
            />
            <span className="text-sm text-slate-600">Entreprise en création</span>
          </label>
          <input className={inputCls} placeholder="Nom de l'entreprise *" value={form.company} onChange={(e) => set('company', e.target.value)} data-testid="quote-client-company" />
          <input className={inputCls} placeholder="Adresse" value={form.company_address} onChange={(e) => set('company_address', e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Code postal" value={form.company_postal_code} onChange={(e) => set('company_postal_code', e.target.value)} />
            <input className={inputCls} placeholder="Ville" value={form.company_city} onChange={(e) => set('company_city', e.target.value)} />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600" data-testid="quote-client-error">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2.5 text-sm rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50">
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="px-4 py-2.5 text-sm rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="quote-client-submit"
        >
          {submitting ? <RefreshCw size={16} className="animate-spin" /> : null}
          Créer et sélectionner
        </button>
      </div>
    </div>
  );
};

export default QuoteClientCreate;
